import { Warning, Error, Info, CheckCircle } from '@mui/icons-material';
import { Box, Grid, Paper, Typography, Card, CardContent, Chip, Button, Divider, Alert } from '@mui/material';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import StatusChip from '@sophocles/components/StatusChip';
import { getAuthUserSelector } from '@sophocles/features/auth/authSelectors';
import { formatDateTime } from '@sophocles/utils/formatUtils';
import { getMeasurementTypeLabel, getUnitAbbreviation } from '@sophocles/utils/measurementUtils';
import { getSensorTypeName } from '@sophocles/utils/sensorTypes';
import LabeledValue from '@sophocles/views/sites/components/LabeledValue';
import StatMiniCard from '@sophocles/views/sites/components/StatMiniCard';
import {
  buildAlarmColorStats,
  buildDeviceInfoFields,
  buildNetworkDetailFields,
  buildReportSummaryStats,
} from '@sophocles/views/sites/utils/statusFieldLists';

// Error boundary for custom renderers
class RenderErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Custom renderer error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const { t } = this.props;
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('status.renderingError')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('status.renderingErrorDesc')}
          </Typography>
          <Button variant="outlined" size="small" onClick={this.props.onShowRaw}>
            {t('status.viewRawData')}
          </Button>
        </Alert>
      );
    }

    return this.props.children;
  }
}

function formatSchedule(schedule, t) {
  if (!schedule || schedule.on_time == null || schedule.off_time == null) {
    return t('general.disabled');
  }

  const formatTime = time => {
    if (typeof time === 'string') {
      return time;
    }

    if (time && typeof time === 'object' && time.event?.label) {
      const offset = time.offset || 0;
      const sign = offset >= 0 ? '+' : '';
      return `${time.event.label} ${sign}${offset}m`;
    }

    return '—';
  };

  return `${formatTime(schedule.on_time)} - ${formatTime(schedule.off_time)}`;
}

function normalizeFeatureEntry(key, value) {
  const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  if (value && typeof value === 'object' && 'label' in value) {
    return [label, String(value.label)];
  }

  return [label, String(value)];
}

function getRelayStateDisplay(state) {
  if (typeof state === 'object' && state !== null && 'label' in state) {
    return String(state.label);
  }
  return String(state ?? '—');
}

function getRelayStateColor(state) {
  const label = getRelayStateDisplay(state).toUpperCase();
  return label.includes('OFF') ? 'bad' : 'info';
}

function formatAlarmState(alarmState, t) {
  // Handle new object format { enabled, active }
  if (alarmState && typeof alarmState === 'object' && 'enabled' in alarmState) {
    if (!alarmState.enabled) {
      return t('general.disabled');
    }
    return alarmState.active ? t('status.alarmActive') : t('status.alarmInactive');
  }
  // Fallback for legacy enum values (0=Disabled, 1=Inactive, 2=Active, 3=Unknown)
  if (typeof alarmState === 'number') {
    const labels = [t('general.disabled'), t('status.alarmInactive'), t('status.alarmActive'), t('general.unknown')];
    return labels[alarmState] ?? t('general.unknown');
  }
  return String(alarmState ?? t('general.unknown'));
}

function formatEpochTime(epochTime, timeZone, user) {
  if (!epochTime) return '—';

  // The epoch timestamp is stored as time IN the unit's timezone
  // We need to manually calculate the offset between unit timezone and browser timezone
  const date = new Date(epochTime * 1000);

  // Get the timezone offset for the unit's timezone at this specific date
  const unitDate = new Date(date.toLocaleString('en-US', { timeZone: timeZone }));
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const unitOffset = unitDate.getTime() - utcDate.getTime();

  // Apply the offset to correct the timezone
  const correctedDate = new Date(date.getTime() - unitOffset);

  return formatDateTime(correctedDate, user);
}

function getAlarmTypeIcon(alarmType) {
  const type = alarmType?.label || alarmType;
  switch (type) {
    case 'NO_VIDEO_OUT':
    case 'VIDEO_LOSS':
      return <Error color="error" />;
    case 'SENSOR_ALARM':
    case 'INTRUSION':
      return <Warning color="warning" />;
    case 'POWER_FAILURE':
    case 'LOW_BATTERY':
      return <Error color="error" />;
    case 'TAMPER':
      return <Warning color="warning" />;
    default:
      return <Info color="info" />;
  }
}

function getAlarmSeverity(alarmType) {
  const type = alarmType?.label || alarmType;
  switch (type) {
    case 'NO_VIDEO_OUT':
    case 'VIDEO_LOSS':
    case 'POWER_FAILURE':
    case 'LOW_BATTERY':
      return 'error';
    case 'SENSOR_ALARM':
    case 'INTRUSION':
    case 'TAMPER':
      return 'warning';
    default:
      return 'info';
  }
}

// Custom renderer for ALARM_NOTIFICATION_V2
function renderAlarmNotificationV2(data, selectedItem, unitTimeZone, user, t) {
  const alarmData = data.data || {};
  const alarmType = data.alarm_type;
  const startTime = data.start_epoch_time;
  const endTime = data.end_epoch_time;

  // Determine alarm state based on start/end times
  const isActiveAlarm = startTime > endTime; // Start after end = active alarm
  const isClearedAlarm = endTime > startTime; // End after start = cleared alarm

  const alertSeverity = isClearedAlarm ? 'success' : getAlarmSeverity(alarmType);
  const alertIcon = isClearedAlarm ? <CheckCircle color="success" /> : getAlarmTypeIcon(alarmType);
  const alarmStatus = isClearedAlarm ? t('status.cleared') : isActiveAlarm ? t('status.alarmActive') : '';

  return (
    <Box>
      {/* Fall back to default rendering for other data */}
      {renderDefaultStatus(data, selectedItem, unitTimeZone, user, t)}

      <Divider sx={{ my: 2 }} />

      {/* Alarm Details */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Typography variant="body2" color="textSecondary">
            {t('status.alarmType')}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
            {formatAlarmState(alarmType, t)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Typography variant="body2" color="textSecondary">
            {t('general.status', 'Status')}
          </Typography>
          {isActiveAlarm ? (
            <Typography color="success.main" variant="body1" sx={{ fontWeight: 'medium' }}>
              {t('status.alarmActive')}
            </Typography>
          ) : (
            <Typography color="error.main" variant="body1" sx={{ fontWeight: 'medium' }}>
              {t('status.cleared')}
            </Typography>
          )}
        </Grid>
      </Grid>

      {/* Time Information */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          {t('status.alarmTimeline')}
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" color="textSecondary">
              {t('general.startTime')}
            </Typography>
            <Typography>{formatEpochTime(startTime, unitTimeZone, user)}</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" color="textSecondary">
              {t('general.endTime')}
            </Typography>
            <Typography>{isClearedAlarm && formatEpochTime(endTime, unitTimeZone, user)}</Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Sensor Data (if available) */}
      {(alarmData.red !== undefined || alarmData.green !== undefined || alarmData.blue !== undefined) && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('status.sensorReadings')}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="textSecondary">
                {t('status.port')}
              </Typography>
              <Typography>{alarmData.sensor_number != null ? alarmData.sensor_number + 1 : '-'}</Typography>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            {buildAlarmColorStats(alarmData, t).map((stat, idx) => (
              <Grid size={4} key={idx}>
                <StatMiniCard {...stat} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
}

// Frame type configuration and renderers
function getFrameTypeConfig(frameType, t) {
  const FRAME_TYPE_CONFIG = {
    ALARM_NOTIFICATION_V2: {
      title: t('status.frameTypes.alarmNotification'),
      customRenderer: true,
    },
    DATA_REPORT_STATUS: {
      title: t('status.frameTypes.sensorDataReport'),
      customRenderer: true,
    },
    STATUS_RESPONSE_UNIFIED: {
      title: t('status.frameTypes.unifiedStatus'),
      customRenderer: false,
    },
    // Add more frame types here as needed
    DEFAULT: {
      title: t('status.frameTypes.deviceSummary'),
      customRenderer: false,
    },
  };

  return FRAME_TYPE_CONFIG[frameType] || FRAME_TYPE_CONFIG.DEFAULT;
}

function renderTitle(frameType, t) {
  const config = getFrameTypeConfig(frameType, t);
  return config.title;
}

function renderContent(frameType, data, selectedItem, unitTimeZone, user, t) {
  switch (frameType) {
    case 'ALARM_NOTIFICATION_V2':
      return renderAlarmNotificationV2(data, selectedItem, unitTimeZone, user, t);
    case 'DATA_REPORT_STATUS':
      return renderDataReportStatus(data, selectedItem, unitTimeZone, user, t);
    // Add more custom renderers here
    default:
      return renderDefaultStatus(data, selectedItem, unitTimeZone, user, t);
  }
}

// Custom renderer for DATA_REPORT_STATUS
function renderDataReportStatus(data, selectedItem, unitTimeZone, user, t) {
  const sensorData = data.sensor_data || [];
  const colorSensorCount = data.color_sensor_count || 0;
  const tempSensorCount = data.temp_sensor_count || 0;
  const intrusionSensorCount = data.intrusion_sensor_count || 0;

  // Count non-common sensors only (exclude type 5)
  const nonCommonSensorCount = sensorData.filter(sensor => sensor.sensor_type !== 5).length;

  return (
    <Box>
      {/* Fall back to default rendering for other data */}
      {renderDefaultStatus(data, selectedItem, unitTimeZone, user, t)}

      <Divider sx={{ mt: 2 }} />

      {/* Report Summary */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          {t('status.reportSummary')}
        </Typography>
        <Grid container spacing={2}>
          {buildReportSummaryStats(
            { colorSensorCount, tempSensorCount, intrusionSensorCount, nonCommonSensorCount },
            t,
          ).map((stat, idx) => (
            <Grid size={{ xs: 6, sm: 3 }} key={idx}>
              <StatMiniCard {...stat} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Detailed Sensor Data */}
      {sensorData.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('status.sensorDetails')}
          </Typography>
          <Grid container spacing={2}>
            {sensorData.map((sensor, idx) => (
              <Grid size={12} key={idx}>
                <Card variant="outlined">
                  <CardContent>
                    {/* Sensor Header */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="h6">
                        {sensor.sensor_type === 5
                          ? t('status.common')
                          : `${t('status.portNumber', { number: sensor.sensor_number + 1 })} - ${getSensorTypeName(sensor.sensor_type, t)}`}
                      </Typography>
                      {sensor.location && (
                        <Chip
                          label={`Location: (${sensor.location.x}, ${sensor.location.y})`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {/* Configuration (for common sensors) */}
                    {sensor.sensor_type === 5 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          {t('general.configuration')}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 6, sm: 3 }}>
                            <Typography variant="caption" color="textSecondary">
                              {t('status.redTolerance')}
                            </Typography>
                            <Typography variant="body2">{sensor.red_tolerance}</Typography>
                          </Grid>
                          <Grid size={{ xs: 6, sm: 3 }}>
                            <Typography variant="caption" color="textSecondary">
                              {t('status.greenTolerance')}
                            </Typography>
                            <Typography variant="body2">{sensor.green_tolerance}</Typography>
                          </Grid>
                          <Grid size={{ xs: 6, sm: 3 }}>
                            <Typography variant="caption" color="textSecondary">
                              {t('status.blueTolerance')}
                            </Typography>
                            <Typography variant="body2">{sensor.blue_tolerance}</Typography>
                          </Grid>
                          <Grid size={{ xs: 6, sm: 3 }}>
                            <Typography variant="caption" color="textSecondary">
                              {t('status.stuckMargin')}
                            </Typography>
                            <Typography variant="body2">{sensor.stuck_margin}</Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}

                    {/* Color Readings Grid */}
                    {(sensor.alarm || sensor.baseline || sensor.watermark || sensor.stuck) && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          {t('status.colorReadings')}
                        </Typography>
                        <Grid container spacing={1}>
                          {/* Header Row */}
                          <Grid size={3}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                              {t('general.type')}
                            </Typography>
                          </Grid>
                          <Grid size={2}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                              {t('status.colorRed')}
                            </Typography>
                          </Grid>
                          <Grid size={2}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                              {t('status.colorGreen')}
                            </Typography>
                          </Grid>
                          <Grid size={2}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                              {t('status.colorBlue')}
                            </Typography>
                          </Grid>
                          <Grid size={3}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                              {t('general.timestamp')}
                            </Typography>
                          </Grid>

                          {/* Alarm Row */}
                          {sensor.alarm && (
                            <>
                              <Grid size={3}>
                                <Typography variant="body2" color="error.main" sx={{ fontWeight: 'medium' }}>
                                  {t('status.alarm')}
                                </Typography>
                              </Grid>
                              <Grid size={2}>
                                <Typography variant="body2">{sensor.alarm.red}</Typography>
                              </Grid>
                              <Grid size={2}>
                                <Typography variant="body2">{sensor.alarm.green}</Typography>
                              </Grid>
                              <Grid size={2}>
                                <Typography variant="body2">{sensor.alarm.blue}</Typography>
                              </Grid>
                              <Grid size={3}>
                                <Typography variant="body2">
                                  {formatEpochTime(sensor.alarm.timestamp, unitTimeZone, user)}
                                </Typography>
                              </Grid>
                            </>
                          )}

                          {/* Baseline Row */}
                          {sensor.baseline && (
                            <>
                              <Grid size={3}>
                                <Typography variant="body2" color="info.main" sx={{ fontWeight: 'medium' }}>
                                  {t('status.baseline')}
                                </Typography>
                              </Grid>
                              <Grid size={2}>
                                <Typography variant="body2">{sensor.baseline.red}</Typography>
                              </Grid>
                              <Grid size={2}>
                                <Typography variant="body2">{sensor.baseline.green}</Typography>
                              </Grid>
                              <Grid size={2}>
                                <Typography variant="body2">{sensor.baseline.blue}</Typography>
                              </Grid>
                              <Grid size={3}>
                                <Typography variant="body2">
                                  {formatEpochTime(sensor.baseline.timestamp, unitTimeZone, user)}
                                </Typography>
                              </Grid>
                            </>
                          )}

                          {/* Watermark Row */}
                          {sensor.watermark && (
                            <>
                              <Grid size={3}>
                                <Typography variant="body2" color="primary.main" sx={{ fontWeight: 'medium' }}>
                                  {t('status.watermark')}
                                </Typography>
                              </Grid>
                              <Grid size={2}>
                                <Typography variant="body2">{sensor.watermark.red}</Typography>
                              </Grid>
                              <Grid size={2}>
                                <Typography variant="body2">{sensor.watermark.green}</Typography>
                              </Grid>
                              <Grid size={2}>
                                <Typography variant="body2">{sensor.watermark.blue}</Typography>
                              </Grid>
                              <Grid size={3}>
                                <Typography variant="body2">
                                  {formatEpochTime(sensor.watermark.timestamp, unitTimeZone, user)}
                                </Typography>
                              </Grid>
                            </>
                          )}

                          {/* Stuck Row */}
                          {sensor.stuck && (
                            <>
                              <Grid size={3}>
                                <Typography variant="body2" color="warning.main" sx={{ fontWeight: 'medium' }}>
                                  {t('status.stuck')}
                                </Typography>
                              </Grid>
                              <Grid size={2}>
                                <Typography variant="body2">{sensor.stuck.red}</Typography>
                              </Grid>
                              <Grid size={2}>
                                <Typography variant="body2">{sensor.stuck.green}</Typography>
                              </Grid>
                              <Grid size={2}>
                                <Typography variant="body2">{sensor.stuck.blue}</Typography>
                              </Grid>
                              <Grid size={3}>
                                <Typography variant="body2">
                                  {sensor.stuck.timestamp
                                    ? formatEpochTime(sensor.stuck.timestamp, unitTimeZone, user)
                                    : '—'}
                                </Typography>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Box>
                    )}

                    {/* Additional Sensor Info */}
                    {(sensor.threshold !== undefined || sensor.size !== undefined) && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          {t('general.additionalInfo')}
                        </Typography>
                        <Grid container spacing={2}>
                          {sensor.threshold !== undefined && (
                            <Grid size={{ xs: 6, sm: 3 }}>
                              <Typography variant="caption" color="textSecondary">
                                {t('general.threshold')}
                              </Typography>
                              <Typography variant="body2">{sensor.threshold}</Typography>
                            </Grid>
                          )}
                          {sensor.size !== undefined && (
                            <Grid size={{ xs: 6, sm: 3 }}>
                              <Typography variant="caption" color="textSecondary">
                                {t('general.size')}
                              </Typography>
                              <Typography variant="body2">{sensor.size}</Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
}

// Default renderer for standard status packets
function renderDefaultStatus(data, selectedItem, unitTimeZone, user, t) {
  const { created_at } = selectedItem;

  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dateUtc = new Date(created_at);
  const localTime = toZonedTime(dateUtc, browserTimeZone);
  const deviceTime = toZonedTime(dateUtc, unitTimeZone);

  const relays = data.relays || [];
  const relayGridCols = relays.length === 1 ? 12 : relays.length === 2 ? 6 : 3;

  const networkDetailFields = buildNetworkDetailFields(data, t);

  return (
    <Box>
      {/* Device Info - only show if we have relevant data */}
      {shouldShowSection(data, 'deviceInfo') && (
        <Grid container spacing={2}>
          {buildDeviceInfoFields(data, selectedItem, t).map((field, idx) => (
            <LabeledValue key={idx} {...field} />
          ))}
        </Grid>
      )}

      {/* Network Details - show additional network info if available */}
      {networkDetailFields.length > 0 && (
        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>
            {t('status.networkDetails')}
          </Typography>
          <Grid container spacing={2}>
            {networkDetailFields.map((field, idx) => (
              <LabeledValue key={idx} {...field} />
            ))}
          </Grid>
        </Box>
      )}

      {/* Timestamps */}
      {shouldShowSection(data, 'timestamps') && (
        <Box mt={3}>
          <Typography variant="body2" color="textSecondary">
            {t('general.timestamp')}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography>{formatDateTime(localTime, user)}</Typography>
              <Chip size="small" label={t('general.yourTimeZone')} sx={{ mt: 0.5 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography>{formatDateTime(deviceTime, user)}</Typography>
              <Chip size="small" label={t('general.unitTimeZone')} sx={{ mt: 0.5 }} />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Features - only show if they exist */}
      {shouldShowSection(data, 'features') && (
        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>
            {t('general.features', 'Features')}
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(data.features || {}).map(([key, value], idx) => {
              const [label, display] = normalizeFeatureEntry(key, value);
              return (
                <Grid size={{ xs: 6, sm: 4, md: 3 }} key={idx}>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                    {label}
                  </Typography>
                  <Typography variant="body2">{display}</Typography>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Relays - only show if they exist */}
      {shouldShowSection(data, 'relays') && (
        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>
            {t('status.relays')}
          </Typography>
          <Grid container spacing={2}>
            {relays.map((relay, idx) => (
              <Grid size={{ xs: 12, sm: relayGridCols }} key={idx}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1">
                        {t('sites.relayNumber', { number: relay.relay_number })}
                      </Typography>
                      <StatusChip
                        label={getRelayStateDisplay(relay.relay_state)}
                        color={getRelayStateColor(relay.relay_state)}
                      />
                    </Box>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="body2" color="textSecondary">
                      {t('general.threshold')}: <strong>{relay.threshold}</strong>
                    </Typography>
                    {relay.live_power != null && (
                      <Typography variant="body2" color="textSecondary">
                        {getMeasurementTypeLabel('live_power', t)}: <strong>{relay.live_power}</strong>
                      </Typography>
                    )}
                    {relay.live_current_ma != null && (
                      <Typography variant="body2" color="textSecondary">
                        {t('status.liveCurrent')}: <strong>{relay.live_current_ma} mA</strong>
                      </Typography>
                    )}
                    {relay.peak_current_ma != null && (
                      <Typography variant="body2" color="textSecondary">
                        {t('status.peakCurrent')}: <strong>{relay.peak_current_ma} mA</strong>
                      </Typography>
                    )}
                    {relay.alarm_state && Object.keys(relay.alarm_state).length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {t('general.alarms')}:
                        </Typography>
                        {Object.entries(relay.alarm_state).map(([alarmKey, alarmValue]) => (
                          <Typography key={alarmKey} variant="body2" color="textSecondary">
                            {alarmKey.replace(/_/g, ' ')}: {formatAlarmState(alarmValue, t)}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                      {t('general.schedules')}:
                    </Typography>
                    {(relay.schedules || []).map((s, i) => (
                      <Typography key={i} variant="body2">
                        {t('general.schedule')} {s.schedule_number}: {formatSchedule(s, t)}
                      </Typography>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Measurements - only show if they exist */}
      {shouldShowSection(selectedItem, 'measurements') && (
        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>
            {t('measurements.title', 'Measurements')}
          </Typography>
          <Grid container spacing={2}>
            {(selectedItem.measurements || []).map((measurement, idx) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2">{getMeasurementTypeLabel(measurement.type, t)}</Typography>
                      {measurement.discriminator && (
                        <Chip size="small" label={measurement.discriminator} variant="outlined" />
                      )}
                    </Box>
                    <Typography variant="body2">
                      {measurement.value} {measurement.unit && getUnitAbbreviation(measurement.unit)}
                    </Typography>
                    {measurement.timestamp && (
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                        {formatDateTime(new Date(measurement.timestamp), user)}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Alarms - only show if they exist */}
      {shouldShowSection(data, 'alarms') && (
        <Box mt={3}>
          <Typography variant="subtitle2" color="textSecondary">
            {t('status.alarmSection')}
          </Typography>
          {Object.entries(data.alarm_state).map(([k, v]) => (
            <Typography key={k} variant="body2">
              {k.replace(/_/g, ' ')}: {formatAlarmState(v, t)}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

// Helper function to check if a section should be displayed
function shouldShowSection(data, sectionName) {
  switch (sectionName) {
    case 'features':
      return data.features && Object.keys(data.features).length > 0;
    case 'relays':
      return data.relays && data.relays.length > 0;
    case 'measurements':
      return data.measurements && Array.isArray(data.measurements) && data.measurements.length > 0;
    case 'alarms':
      return data.alarm_state && Object.keys(data.alarm_state).length > 0;
    case 'deviceInfo':
      return true; // Always show device info
    case 'timestamps':
      return true; // Always show timestamps
    default:
      return true;
  }
}

function GenericStatusDetail({ selectedItem, unitTimeZone }) {
  const [showRaw, setShowRaw] = useState(false);
  const { t } = useTranslation();
  const user = useSelector(getAuthUserSelector);

  if (!selectedItem) return null;

  const { data = {}, frame_type } = selectedItem;

  if (showRaw) {
    return (
      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">{t('status.rawJsonView')}</Typography>
          <Button variant="outlined" size="small" onClick={() => setShowRaw(false)}>
            {t('status.backToSummary')}
          </Button>
        </Box>
        <pre style={{ overflowX: 'auto' }}>{JSON.stringify(selectedItem, null, 2)}</pre>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      {/* Dynamic Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">{renderTitle(frame_type, t)}</Typography>
        <Button variant="outlined" size="small" onClick={() => setShowRaw(true)}>
          {t('status.viewRaw')}
        </Button>
      </Box>

      {/* Dynamic Content Rendering with Error Boundary */}
      <RenderErrorBoundary onShowRaw={() => setShowRaw(true)} t={t}>
        {renderContent(frame_type, data, selectedItem, unitTimeZone, user, t)}
      </RenderErrorBoundary>
    </Paper>
  );
}

export default GenericStatusDetail;
